import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useParams } from 'react-router';
import { CertificateStatus } from 'softcat:components/certificate-status.tsx';
import { Form } from 'softcat:components/form.tsx';
import { Spinner } from 'softcat:components/spinner.tsx';
import { useTerritory } from 'softcat:hooks/use-territory.ts';
import { CertificateService } from 'softcat:services/certificate.ts';
import useSWR from 'swr';
import * as z from 'zod';

export const CertificatesEdit = () => {
    const navigate = useNavigate();
    const { activeTerritory } = useTerritory();
    const { id = '' } = useParams<{ id: string }>();
    const [submitting, setSubmitting] = useState(false);
    const [onEditError, setOnEditError] = useState('');
    const [formValues, setFormValues] = useState({ active: false });

    const EditCertificateSchema = z.object({
        active: z.boolean(),
    });

    const {
        data: certificateData,
        error: certificateDataError,
        isLoading: certificateDataIsLoading,
    } = useSWR({ id, key: `${activeTerritory}-CertificateServiceFindOne` }, CertificateService.findOne);

    useEffect(() => {
        if (certificateData) {
            setFormValues({
                active: certificateData.active,
            });
        }
    }, [certificateData]);

    const onSubmit = async (values: z.infer<typeof EditCertificateSchema>) => {
        setSubmitting(true);
        setOnEditError('');

        if (certificateData) {
            try {
                await (values.active ? CertificateService.activate({ id: certificateData.id }) : CertificateService.deactivate({ id: certificateData.id }));

                setSubmitting(false);

                return navigate('/certificates');
            } catch (error) {
                setSubmitting(false);
                setOnEditError((error as Error).message);
            }
        }
    };

    return (
        <Form<z.infer<typeof EditCertificateSchema>> defaultValues={formValues} onSubmit={onSubmit} resolver={zodResolver(EditCertificateSchema)}>
            <div className="space-y-3 px-4">
                <Form.Header
                    description={<FormattedMessage id={'CERTIFICATES_EDIT_PAGE_DESCRIPTION'} />}
                    title={<FormattedMessage id={'CERTIFICATES_EDIT_PAGE_HEADING'} />}
                />

                {certificateDataError && (
                    <p className="mt-2 text-sm text-gray-700">
                        <FormattedMessage id={'CERTIFICATES_EDIT_PAGE_ERROR_LOADING_DATA'} />
                    </p>
                )}

                {certificateDataIsLoading && <Spinner />}

                {certificateData && (
                    <>
                        <Form.StaticValue label={<FormattedMessage id={'CERTIFICATES_FORM_NAME_LABEL'} />} value={certificateData.name} />

                        <Form.StaticValue label={<FormattedMessage id={'CERTIFICATES_FORM_TYPE_LABEL'} />} value={certificateData.type} />

                        <Form.StaticValue
                            label={<FormattedMessage id={'CERTIFICATES_EDIT_PAGE_FORM_EXPIRY_LABEL'} />}
                            value={<CertificateStatus certificateExpiry={certificateData.expiresAt} />}
                        />

                        <Form.Checkbox label={<FormattedMessage id={'CERTIFICATES_FORM_ACTIVE_LABEL'} />} name={'active'} />

                        <Form.Footer
                            isSubmitting={submitting}
                            onCancel={() => {
                                return navigate('/certificates');
                            }}
                            submitError={onEditError && <FormattedMessage id={'CERTIFICATES_EDIT_PAGE_FORM_SUBMIT_ERROR'} values={{ error: onEditError }} />}
                            submitText={<FormattedMessage id={'BUTTON_UPDATE'} />}
                        />
                    </>
                )}
            </div>
        </Form>
    );
};
